import React, { useState } from 'react';
import { Certificate } from '../../api/productService';
import { downloadCertificateFile, isDownloadSupported } from '../../api/certificateService';
import './CertificatePopup.css';

interface CertificatePopupProps {
  certificate: Certificate | null;
  isOpen: boolean;
  onClose: () => void;
  onGetCertificate?: (certificate: Certificate) => void;
}

const CertificatePopup: React.FC<CertificatePopupProps> = ({
  certificate,
  isOpen,
  onClose,
  onGetCertificate
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  if (!isOpen || !certificate) {
    return null;
  }

  const handleGetCertificate = async () => {
    if (!certificate) return;

    setIsDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(false);

    try {
      // Call the parent callback if provided
      if (onGetCertificate) {
        onGetCertificate(certificate);
      }

      // Check if browser supports downloads
      if (!isDownloadSupported()) {
        throw new Error('Your browser does not support automatic downloads. Please check your browser settings.');
      }

      // Download the certificate
      const filename = `${certificate?.certNumber || 'Course'}-certificate.pdf`;
      await downloadCertificateFile(certificate.id, filename);

      // Show success message
      setDownloadSuccess(true);

      // Optional: Close popup after successful download
      // onClose();
    } catch (error: any) {
      console.error('Error downloading certificate:', error);
      setDownloadError(
        error.response?.data?.message ||
        error.message ||
        'Failed to download certificate. Please try again.'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="certificate-popup-overlay" onClick={handleOverlayClick}>
      <div className="certificate-popup">
        <button className="certificate-popup-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="certificate-popup-content">
          <div className="certificate-icon-large">
            <img src={'/images/completed-medal.png'} alt={'medal'}/>
          </div>

          <h2 className="certificate-popup-title">Course completed!</h2>
          <p className="certificate-popup-subtitle">
            Congratulations! You've successfully completed the course.
          </p>

          {downloadSuccess && (
            <div className="certificate-download-success">
              Certificate downloaded successfully! Check your downloads folder.
            </div>
          )}

          {downloadError && (
            <div className="certificate-download-error">
              {downloadError}
            </div>
          )}

          <button
            className="certificate-get-button"
            onClick={handleGetCertificate}
            disabled={isDownloading}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 2V12M10 12L14 8M10 12L6 8M3 16L17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {isDownloading ? 'DOWNLOADING...' : 'GET MY CERTIFICATE'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificatePopup;
